import React, { useState, useEffect, useRef } from "react";
import "./HealthInsurance.css";
import { Link } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import { useDispatch, useSelector } from "react-redux";
import {
  submitEnquiryForm,
  resetEnquiryFormState,
} from "../../store/slices/enquiryFormSlice";

const HealthInsurance = () => {
  const [formData, setFormData] = useState({
    fullname: "", // Changed from fullName to match API
    phone: "",
    email: "",
    referral_name: "",
    referral_email: "",
    message: "", // Text area for health insurance information
  });

  const [acceptTerms, setAcceptTerms] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState("");
  const [submitStatus, setSubmitStatus] = useState("");
  const [openAccordion, setOpenAccordion] = useState([
    1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15,
  ]);
  const [recaptchaToken, setCaptchaValue] = useState(null);
  const [errors, setErrors] = useState({});
  const recaptchaRef = useRef(null);
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleRecaptchaChange = (value) => {
    setCaptchaValue(value);

    if (errors.captcha) {
      setErrors((prev) => ({
        ...prev,
        captcha: "",
      }));
    }
  };
  const toggleAccordion = (index) => {
    setOpenAccordion((prev) =>
      prev.includes(index)
        ? prev.filter((item) => item !== index)
        : [...prev, index],
    );
  };

  // Format backend errors
  const formatBackendErrors = (errors) => {
    if (!errors || typeof errors !== "object")
      return "An error occurred. Please try again.";

    return Object.entries(errors)
      .flatMap(([field, messages]) =>
        messages.map(
          (m) =>
            `${field.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}: ${m}`,
        ),
      )
      .join("\n");
  };

  // Form validation
  const validateForm = () => {
    const phoneRegex = /^[0-9]{10}$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formData.fullname || !formData.email || !formData.phone)
      return "Please fill in all required fields";

    if (!phoneRegex.test(formData.phone))
      return "Please enter a valid 10-digit mobile number";

    if (!emailRegex.test(formData.email))
      return "Please enter a valid email address";

    if (formData.referral_email && !emailRegex.test(formData.referral_email))
      return "Please enter a valid referral email address";

    if (!acceptTerms) return "Please accept the terms and conditions";
    if (!recaptchaToken) {
      return "Please verify captcha";
    }

    return null;
  };

  // Clear message after timeout
  const clearMessageAfterTimeout = () => {
    setTimeout(() => {
      setSubmitMessage("");
      setSubmitStatus("");
    }, 5000);
  };

  const dispatch = useDispatch();
  const { success, error, message } = useSelector((state) => state.enquiryForm);

  const handleSubmit = (e) => {
    e.preventDefault();

    const errorMsg = validateForm();
    if (errorMsg) {
      setSubmitMessage(errorMsg);
      setSubmitStatus("error");
      clearMessageAfterTimeout();
      return;
    }
    if (!recaptchaToken) {
      return "Please verify captcha";
    }

    setIsSubmitting(true);
    setSubmitMessage("");

    const payload = {
      fullname: formData.fullname.trim(),
      email: formData.email.trim().toLowerCase(),
      mobile: formData.phone.trim(),

      require_service: "Health Insurance",
      page_url: window.location.href,
      recaptcha_token: recaptchaToken,

      ...(formData.referral_name && {
        reference_name: formData.referral_name.trim(),
      }),
      ...(formData.referral_email && {
        reference_email: formData.referral_email.trim().toLowerCase(),
      }),
      ...(formData.message && {
        additional_data: JSON.stringify({
          message: formData.message.trim(),
        }),
      }),
    };

    dispatch(submitEnquiryForm(payload));
  };
  useEffect(() => {
    if (success) {
      setSubmitMessage("Form submitted successfully ✅");
      setSubmitStatus("success");

      setFormData({
        fullname: "",
        phone: "",
        email: "",
        referral_name: "",
        referral_email: "",
        message: "",
      });

      setAcceptTerms(false);

      setCaptchaValue(null);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }

      clearMessageAfterTimeout();
      setIsSubmitting(false);

      dispatch(resetEnquiryFormState());
    }
  }, [success, message]); // ✅ add message
  useEffect(() => {
    if (error) {
      setSubmitMessage(error);
      setSubmitStatus("error");

      clearMessageAfterTimeout();
      setIsSubmitting(false);
    }
  }, [error]);

  const accordionData = [
    {
      id: 1,
      title: "What is Health Insurance?",
      content: [
        "Health Insurance is a product by Insurance Companies to cover the medical and surgical expenses incurred by the insurer. Health is the most unpredictable aspect of one's life. Anybody can encounter a serious health issue at any stage of life.",
        "If not any health issue, it could be hospitalisation due to an accident. Meeting medical expenses which appear out-of-the-blue is not in the reach of everyone. At least not at present times, when the cost of medical treatment is soaring high day by day.",
        "The product that covers the expenses that arise out of such emergencies is Health Insurance. Periodical payment of premium as stipulated by the Insurance Company has to be made.",
        "The insurance company either pays the medical expenses incurred directly to the hospital or will reimburse the expenses to the insured individual. Some Health Insurance companies offer a cover for as many as 80 and above surgical procedures and as many as 30 and above critical illness.",
      ],
    },
    {
      id: 2,
      title: "Features and Advantages of Health Insurance Policy",
      features: [
        "Cover for Expenses Before and After Hospitalisation",
        "Treatment Without Payment – Cashless Treatment",
        "Critical illness plans covering diseases such as brain tumour, cancer, kidney problems",
        "Plans that cover pre-existing diseases",
        "Family floater plans for all family members",
        "Special plans available for senior citizens up to the age of 65",
      ],
      content:
        "With a Health Insurance Policy, you can get medical treatment in the best hospitals without worrying about treatment costs. Medical emergencies can be handled easily even when you are in a difficult financial situation.",
    },
    {
      id: 3,
      title: "Best Health Insurance Policies in India",
      companies: [
        {
          name: "Max Bupa Health Insurance – Health Companion",
          features: [
            "Coverage up to ₹1 Crore",
            "4,000+ network hospitals",
            "Lifetime renewal",
            "No cap on room rent",
          ],
        },
        {
          name: "Royal Sundaram – Lifeline Health Insurance",
          features: [
            "Plans from ₹2L to ₹1.5 Cr",
            "Day-care treatments",
            "No claim bonus",
            "Ayush treatment covered",
          ],
        },
        {
          name: "Apollo Munich Health Insurance",
          features: [
            "Sum insured ₹3L to ₹50L",
            "100% restoration",
            "Premium discounts",
            "4,500+ hospitals",
          ],
        },
        {
          name: "Star Health Insurance",
          features: [
            "Individual & Family plans",
            "Senior citizen plans",
            "OPD coverage",
            "Lifetime renewal",
          ],
        },
      ],
    },
    {
      id: 4,
      title: "Which Medical Insurance is Best for Family?",
      plans: [
        "Star Family Health Optima",
        "Max Bupa Health Companion",
        "HDFC Health Suraksha",
        "Apollo Munich Optima Restore Family Plan",
        "SBI Life Smart Health Insurance",
        "Bajaj Allianz Family Floater – Health Guard",
      ],
    },
    {
      id: 5,
      title: "Difference Between Mediclaim and Health Insurance",
      table: {
        headers: ["Health Insurance", "Mediclaim"],
        rows: [
          [
            "Covers pre and post hospitalisation expenses",
            "Covers only hospitalisation expenses",
          ],
          [
            "Ambulance services and lost income covered",
            "Only hospitalisation cover",
          ],
          [
            "Critical illness cover for 30+ diseases",
            "Critical illness not covered",
          ],
          ["Sum assured up to ₹6 Crores", "Sum assured around ₹5 Lakhs"],
          [
            "Single claim for critical illness",
            "Multiple claims until amount exhausted",
          ],
        ],
      },
    },
    {
      id: 6,
      title: "How to Choose the Best Health Insurance Policy?",
      tips: [
        "Choose maximum coverage with higher treatment limit",
        "Consider family floater policies for family coverage",
        "Check waiting period for pre-existing diseases (2–4 years)",
        "Choose policies with lifetime renewal option",
        "Select companies with high claim settlement ratio",
        "Check sub-limits on room rent and hospital expenses",
        "Choose insurers with wide network of hospitals",
        "Compare multiple plans for maximum benefits",
        "Understand exclusions carefully",
        "Add critical illness rider for extra coverage",
      ],
    },
    {
      id: 7,
      title: "Importance of Health Insurance in India",
      reasons: [
        "Covers medical expenses for hospitalisation and treatment",
        "Keeps you financially prepared for sudden emergencies",
        "Affordable premium with strong financial security",
        "Easy and hassle-free claim settlement process",
        "Low chance of claim rejection with valid reasons",
      ],
    },
    {
      id: 8,
      title: "Claim Ratio of Top Insurance Companies",
      ratios: [
        { company: "Bharathi Axa General Insurance", ratio: "96.85%" },
        { company: "Cholamandalam MS General Insurance", ratio: "72.91%" },
        { company: "Bajaj Allianz General Insurance", ratio: "76.88%" },
        { company: "Apollo Munich Health Insurance", ratio: "54.99%" },
        { company: "ICICI Lombard General Insurance", ratio: "80.38%" },
        { company: "Max Bupa Health Insurance", ratio: "51.96%" },
        { company: "Star Health and Allied Insurance", ratio: "60.51%" },
      ],
    },
    {
      id: 9,
      title: "Claim Settlement for Non-Network Hospitals",
      content:
        "When treatment is taken in a hospital which is not included in the cashless treatment hospital list, the claim settlement will be by reimbursement only. You will have to initially bear the medical expenses and then claim for reimbursement from the insurance company.",
    },
    {
      id: 10,
      title: "Geographical Coverage of Policy",
      content:
        "You should ascertain before availing the policy if there are any geographical limitations stated in the policy. Some policies offer cover for treatment abroad. Look for a policy which is PAN India and provides cover for treatment abroad if required.",
    },
    {
      id: 11,
      title: "How to Avail Cashless Treatment?",
      content:
        "To avail cashless treatment, take treatment in a network hospital. Obtain pre-authorisation 48 hours before admission or within 24 hours in emergency. The expenses will be directly settled by the Insurance Company.",
    },
    {
      id: 12,
      title: "Coverage for Pre-existing Illness",
      content:
        "Yes. Pre-existing illness is covered by Health Insurance policies but with a waiting period of 2 to 4 years. Some insurance companies offer shorter waiting periods. Choose a policy with lesser waiting period if you need coverage for pre-existing conditions.",
    },
    {
      id: 13,
      title: "Emergency Hospitalisation Contact",
      content:
        "Before buying Health Insurance, make enquiries about the emergency hospitalisation policy and whom to contact. There will be an exclusive representative or customer service desk handling these issues. Be aware of the contact persons who will guide you through such situations.",
    },
    {
      id: 14,
      title: "Documents Required for Claim Settlement",
      content:
        "Required documents include identification documents of the insured member, hospital bills, health card, claim form. For cashless treatment, submit pre-authorisation from the insurance company to the TPA.",
    },
    {
      id: 15,
      title: "Ensuring Smooth Claim Settlement",
      content:
        "Before availing Health Insurance, ascertain the claim settlement process and time taken. Choose insurance companies offering cashless treatment rather than only reimbursement. This ensures you don't need to bear the burden of medical bills upfront.",
    },
  ];

  return (
    <div className="health-insurance-wrapper">
      {/* Hero Section with Medical Theme */}
      <section className="hi-hero-section">
        <div className="hi-container">
          <div className="hi-hero-content">
            <div className="hi-hero-text">
              <span className="hi-badge">Secure Your Health</span>
              <h1 className="hi-main-title">
                <span className="hi-title-line">Health</span>
                <span className="hi-title-line accent">Insurance</span>
              </h1>
              <p className="hi-hero-description">
                Compare policies of top insurers and buy instant health
                insurance plan to cover all medical needs of self and family
                members. Get affordable health insurance cover for
                hospitalization, surgery & much more.
              </p>
              <div className="hi-cta-group">
                <button className="hi-btn hi-btn-primary">Compare Plans</button>
                <button className="hi-btn hi-btn-outline">Learn More</button>
              </div>
              <div className="hi-stats">
                <div className="hi-stat-item">
                  <span className="hi-stat-value">50+</span>
                  <span className="hi-stat-label">Insurers</span>
                </div>
                <div className="hi-stat-item">
                  <span className="hi-stat-value">10k+</span>
                  <span className="hi-stat-label">Hospitals</span>
                </div>
                <div className="hi-stat-item">
                  <span className="hi-stat-value">24/7</span>
                  <span className="hi-stat-label">Support</span>
                </div>
              </div>
            </div>
            <div className="hi-hero-visual">
              <div className="hi-image-wrapper">
                <img
                  src="/images/insurance/health1.png"
                  alt="Health Insurance"
                  className="hi-main-image"
                />
              </div>
            </div>
          </div>
        </div>
        <div className="hi-shape-divider">
          <svg
            data-name="Layer 1"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1200 120"
            preserveAspectRatio="none"
          >
            <path
              d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"
              className="hi-shape-fill"
            ></path>
          </svg>
        </div>
      </section>

      {/* Main Content with Form */}
      <section className="hi-main-section">
        <div className="hi-container">
          <div className="hi-main-grid">
            {/* Left Column - Benefits */}
            <div className="hi-benefits-column">
              <div className="hi-section-header">
                <span className="hi-section-tag">Why Choose Us</span>
                <h2 className="hi-section-title">Complete Health Protection</h2>
                <p className="hi-section-desc">
                  Get affordable health protection plan from top insurers with
                  maximum benefits
                </p>
              </div>

              <div className="hi-benefits-grid">
                <div className="hi-benefit-card">
                  <div className="hi-benefit-icon tax-icon">💰</div>
                  <h3>Tax Benefits</h3>
                  <p>Save up to Rs. 75,000 under Section 80D</p>
                </div>
                <div className="hi-benefit-card">
                  <div className="hi-benefit-icon hospital-icon">🏨</div>
                  <h3>Cashless Hospitalization</h3>
                  <p>10,000+ network hospitals across India</p>
                </div>
                <div className="hi-benefit-card">
                  <div className="hi-benefit-icon checkup-icon">🩺</div>
                  <h3>Free Health Check-up</h3>
                  <p>Annual preventive health check-ups included</p>
                </div>
                <div className="hi-benefit-card">
                  <div className="hi-benefit-icon family-icon">👨‍👩‍👧‍👦</div>
                  <h3>Family Coverage</h3>
                  <p>Protect yourself and your family members</p>
                </div>
              </div>

              <div className="hi-testimonial-card">
                <div className="hi-testimonial-quote">"</div>
                <p className="hi-testimonial-text">
                  The cashless claim process was smooth and hassle-free. Got
                  approval in just 30 minutes!
                </p>
                <div className="hi-testimonial-author">
                  <div className="hi-author-avatar">RK</div>
                  <div>
                    <h4>Rajesh Kumar</h4>
                    <span>Policyholder since 2020</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Form with API Integration */}
            <div className="hi-form-column">
              <div className="hi-form-card">
                <div className="hi-form-header">
                  <h3>Get Free Quote</h3>
                  <p>No spam. Instant response.</p>
                </div>

                {/* Submit Message Display */}
                {submitMessage && (
                  <div className={`hi-submit-message ${submitStatus}`}>
                    <span className="hi-message-icon">
                      {submitStatus === "success" ? "✓" : "⚠️"}
                    </span>
                    {submitMessage}
                    <button
                      className="hi-close-message"
                      onClick={() => {
                        setSubmitMessage("");
                        setSubmitStatus("");
                      }}
                    >
                      ×
                    </button>
                  </div>
                )}

                <form className="hi-form" onSubmit={handleSubmit}>
                  {/* Full Name */}
                  <div className="hi-input-group full-width">
                    <label>
                      Full Name <span className="hi-required">*</span>
                    </label>
                    <input
                      type="text"
                      name="fullname"
                      value={formData.fullname}
                      onChange={handleInputChange}
                      placeholder="Enter your full name"
                      required
                    />
                  </div>

                  {/* Phone Number */}
                  <div className="hi-input-group full-width">
                    <label>
                      Phone Number <span className="hi-required">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="9737304440"
                      required
                      pattern="[0-9]{10}"
                      title="Please enter exactly 10 digits"
                    />
                  </div>

                  {/* Email */}
                  <div className="hi-input-group full-width">
                    <label>
                      Email Address <span className="hi-required">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="loansviakrishna@gmail.com
"
                      required
                    />
                  </div>

                  {/* Referral Name */}
                  <div className="hi-input-group full-width">
                    <label>Referral Name (Optional)</label>
                    <input
                      type="text"
                      name="referral_name"
                      value={formData.referral_name}
                      onChange={handleInputChange}
                      placeholder="Who referred you?"
                    />
                  </div>

                  {/* Referral Email */}
                  <div className="hi-input-group full-width">
                    <label>Referral Email (Optional)</label>
                    <input
                      type="email"
                      name="referral_email"
                      value={formData.referral_email}
                      onChange={handleInputChange}
                      placeholder="loansviakrishna@gmail.com
"
                    />
                  </div>

                  {/* Message Text Area */}
                  <div className="hi-input-group full-width">
                    <label>Tell us about your Health Insurance needs</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Please share any specific requirements or questions about health insurance..."
                      rows="4"
                      className="hi-textarea"
                    />
                  </div>

                  {/* recaptcha */}

                  <div className="li-recaptcha">
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey="6Ldk_IksAAAAAO8Aq02xgOPeBlszLT2K30hdJCjM"
                      onChange={handleRecaptchaChange}
                      // ✅ ADD THIS (important)
                      onExpired={() => {
                        setCaptchaValue(null);
                        setErrors((prev) => ({
                          ...prev,
                          captcha: "Captcha expired, please verify again",
                        }));
                      }}
                      // ✅ OPTIONAL (network issue handle)
                      onErrored={() => {
                        setCaptchaValue(null);
                        setErrors((prev) => ({
                          ...prev,
                          captcha: "Captcha failed to load. Try again.",
                        }));
                      }}
                    />
                  </div>
                  {/* Terms and Conditions */}
                  <div className="hi-terms-group">
                    <input
                      type="checkbox"
                      id="hi-terms"
                      checked={acceptTerms}
                      onChange={(e) => setAcceptTerms(e.target.checked)}
                    />
                    <label htmlFor="hi-terms">
                      I agree to receive loan updates and promotional from{" "}
                      <strong>Krishana Finconcept</strong> via WhatsApp and
                      other accept the{" "}
                      <a
                        href="/privacy-security-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Terms & Conditions
                      </a>
                    </label>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    className="hi-submit-btn"
                    disabled={!acceptTerms || isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="hi-spinner"></span>
                        Submitting...
                      </>
                    ) : (
                      <>
                        Get Free Quotes
                        <span className="hi-btn-arrow">→</span>
                      </>
                    )}
                  </button>

                  <p className="hi-form-footer">
                    *No spam. 100% privacy guaranteed
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="hi-partners-section">
        <div className="hi-container">
          <h2 className="hi-partners-title">
            Trusted by India's Top Insurance Partners
          </h2>
          <div className="hi-partners-marquee">
            <div className="hi-partners-track">
              {[
                "bajaj",
                "axis",
                "bandhan",
                "rbl",
                "bob",
                "canara",
                "central",
                "city",
                "icici",
                "hdfc",
              ].map((partner, index) => (
                <div key={index} className="hi-partner-item">
                  <img
                    src={`/bank-logos/${partner}.png`}
                    alt={`${partner} logo`}
                  />
                </div>
              ))}
              {/* Duplicate for infinite scroll */}
              {[
                "bajaj",
                "axis",
                "bandhan",
                "rbl",
                "bob",
                "canara",
                "central",
                "city",
              ].map((partner, index) => (
                <div key={`dup-${index}`} className="hi-partner-item">
                  <img
                    src={`/bank-logos/${partner}.png`}
                    alt={`${partner} logo`}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Accordion Section */}
      <section className="hi-accordion-section">
        <div className="hi-container">
          <div className="hi-section-header centered">
            <span className="hi-section-tag">Knowledge Base</span>
            <h2 className="hi-section-title">Everything You Need to Know</h2>
            <p className="hi-section-desc">
              Comprehensive guide to make informed decisions about your health
              insurance
            </p>
          </div>

          <div className="hi-accordion-container">
            {accordionData.map((item) => (
              <div
                key={item.id}
                className={`hi-accordion-item ${openAccordion.includes(item.id) ? "active" : ""}`}
              >
                <div
                  className="hi-accordion-header"
                  onClick={() => toggleAccordion(item.id)}
                >
                  <h3>{item.title}</h3>
                  <span className="hi-accordion-icon">
                    {openAccordion.includes(item.id) ? "−" : "+"}
                  </span>
                </div>
                <div className="hi-accordion-content">
                  {item.content && (
                    <div className="hi-content-text">
                      {Array.isArray(item.content) ? (
                        item.content.map((para, idx) => <p key={idx}>{para}</p>)
                      ) : (
                        <p>{item.content}</p>
                      )}
                    </div>
                  )}

                  {item.features && (
                    <ul className="hi-feature-list">
                      {item.features.map((feature, idx) => (
                        <li key={idx}>{feature}</li>
                      ))}
                    </ul>
                  )}

                  {item.companies && (
                    <div className="hi-companies-grid">
                      {item.companies.map((company, idx) => (
                        <div key={idx} className="hi-company-card">
                          <h4>{company.name}</h4>
                          <ul>
                            {company.features.map((feature, fidx) => (
                              <li key={fidx}>{feature}</li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}

                  {item.plans && (
                    <ul className="hi-plans-list">
                      {item.plans.map((plan, idx) => (
                        <li key={idx}>{plan}</li>
                      ))}
                    </ul>
                  )}

                  {item.table && (
                    <div className="hi-table-wrapper">
                      <table className="hi-comparison-table">
                        <thead>
                          <tr>
                            {item.table.headers.map((header, idx) => (
                              <th key={idx}>{header}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {item.table.rows.map((row, ridx) => (
                            <tr key={ridx}>
                              {row.map((cell, cidx) => (
                                <td key={cidx}>{cell}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {item.tips && (
                    <ol className="hi-tips-list">
                      {item.tips.map((tip, idx) => (
                        <li key={idx}>{tip}</li>
                      ))}
                    </ol>
                  )}

                  {item.reasons && (
                    <ul className="hi-reasons-list">
                      {item.reasons.map((reason, idx) => (
                        <li key={idx}>{reason}</li>
                      ))}
                    </ul>
                  )}

                  {item.ratios && (
                    <div className="hi-ratios-grid">
                      {item.ratios.map((item, idx) => (
                        <div key={idx} className="hi-ratio-item">
                          <span className="hi-company-name">
                            {item.company}
                          </span>
                          <span className="hi-ratio-value">{item.ratio}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default HealthInsurance;
